/**
 * Bake tool-prep effects (resize, shadow, outline, background fill, watermark) to PNG blobs.
 */

import { compositeEditorToPngBlob } from "../../../utils/editorExportComposite";
import { buildDropShadowFilter, getShadowBleedPx } from "../../../utils/dropShadowFilter";
import { generateOutlineDataUrl } from "../../../utils/generateOutlineImage";
import { getTransform, parsePresetPixelSize } from "../../../utils/getTransform";
import { TOOL_PREP_PREVIEW_BG } from "../../../utils/editorEntrySession";
import { composeBlurPrepCanvas } from "./applyBlurEffect";

function loadImage(url) {
  return new Promise((resolve, reject) => {
    if (!url) {
      reject(new Error("Missing image URL"));
      return;
    }
    const img = new Image();
    if (
      typeof url === "string" &&
      /^https?:\/\//i.test(url) &&
      !url.startsWith("blob:") &&
      !url.startsWith("data:")
    ) {
      img.crossOrigin = "anonymous";
    }
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Failed to load image for export"));
    img.src = url;
  });
}

function encodeCanvasToPngBlob(canvas) {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob);
        else reject(new Error("Could not export image"));
      },
      "image/png",
      1
    );
  });
}

function resolvePrepExportBackground(previewBg, backgroundColor, uploadedImageUrl) {
  if (
    previewBg === TOOL_PREP_PREVIEW_BG.WHITE ||
    previewBg === TOOL_PREP_PREVIEW_BG.COLOR
  ) {
    return {
      backgroundColor: backgroundColor || "#FFFFFF",
      backgroundImageUrl: null,
    };
  }
  if (previewBg === TOOL_PREP_PREVIEW_BG.ORIGINAL && uploadedImageUrl) {
    return {
      backgroundColor: "transparent",
      backgroundImageUrl: uploadedImageUrl,
    };
  }
  return { backgroundColor: "transparent", backgroundImageUrl: null };
}

async function buildSubjectEffectBlob(imageUrl, options = {}) {
  const {
    shadowState = { enabled: false },
    outlineState = { enabled: false },
    previewBg,
    backgroundColor,
    uploadedImageUrl,
  } = options;

  const img = await loadImage(imageUrl);
  const iw = img.naturalWidth || img.width;
  const ih = img.naturalHeight || img.height;
  if (!iw || !ih) {
    throw new Error("Invalid image dimensions");
  }

  let outlineImageUrl = null;
  let outlineBleedPx = 0;
  if (outlineState?.enabled && Number(outlineState.width) > 0) {
    const outlineResult = await generateOutlineDataUrl(imageUrl, {
      width: outlineState.width,
      color: outlineState.color,
    });
    outlineImageUrl = outlineResult?.dataUrl || null;
    outlineBleedPx = outlineResult?.bleedPx || 0;
  }

  const { backgroundColor: bgColor, backgroundImageUrl } = resolvePrepExportBackground(
    previewBg,
    backgroundColor,
    uploadedImageUrl
  );

  // Pad canvas so outward stroke (and optional blur) is not clipped at image edges.
  const blurPx = Math.max(0, Number(outlineState?.blur) || 0);
  const pad = Math.max(0, Math.ceil(outlineBleedPx + blurPx));
  const cw = iw + pad * 2;
  const ch = ih + pad * 2;

  return compositeEditorToPngBlob({
    imageUrl,
    backgroundColor: bgColor,
    backgroundImageUrl,
    canvasSize: { width: cw, height: ch },
    objectSize: { width: iw, height: ih },
    objectPosition: { x: pad, y: pad },
    objectRotation: 0,
    objectFlip: { x: 1, y: 1 },
    cropState: { x: 0, y: 0, width: iw, height: ih },
    shadowState,
    outlineState,
    outlineImageUrl,
    outlineBleedPx,
    softOverflow: true,
    exportSize: "standard",
  });
}

/**
 * @param {string} imageUrl
 * @param {{ preset: object, resizeMode?: 'fit'|'fill' }} options
 */
export async function exportResizePrepImageBlob(
  imageUrl,
  { preset, resizeMode = "fit" } = {}
) {
  const parsed = parsePresetPixelSize(preset);
  if (!parsed) {
    throw new Error("Choose a resize preset first.");
  }

  const img = await loadImage(imageUrl);
  const iw = img.naturalWidth || img.width;
  const ih = img.naturalHeight || img.height;
  const cw = parsed.width;
  const ch = parsed.height;

  const canvas = document.createElement("canvas");
  canvas.width = cw;
  canvas.height = ch;
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    throw new Error("Could not create export canvas");
  }

  ctx.clearRect(0, 0, cw, ch);

  if (resizeMode === "fill") {
    const scale = Math.max(cw / iw, ch / ih);
    const drawW = iw * scale;
    const drawH = ih * scale;
    const dx = (cw - drawW) / 2;
    const dy = (ch - drawH) / 2;
    ctx.drawImage(img, dx, dy, drawW, drawH);
  } else {
    const t = getTransform(iw, ih, cw, ch, "fit");
    ctx.drawImage(img, t.x, t.y, t.width, t.height);
  }

  return encodeCanvasToPngBlob(canvas);
}

export async function exportShadowPrepImageBlob(imageUrl, options = {}) {
  const {
    shadowState = { enabled: false },
    outlineState = { enabled: false },
    previewBg,
    backgroundColor,
    uploadedImageUrl,
  } = options;

  if (outlineState?.enabled && Number(outlineState.width) > 0) {
    return buildSubjectEffectBlob(imageUrl, options);
  }

  const img = await loadImage(imageUrl);
  const iw = img.naturalWidth || img.width;
  const ih = img.naturalHeight || img.height;
  if (!iw || !ih) {
    throw new Error("Invalid image dimensions");
  }

  const shadowEnabled = Boolean(shadowState?.enabled);
  const pad = shadowEnabled ? getShadowBleedPx(shadowState) : 0;
  const cw = iw + pad * 2;
  const ch = ih + pad * 2;

  const { backgroundColor: bgColor, backgroundImageUrl } =
    resolvePrepExportBackground(previewBg, backgroundColor, uploadedImageUrl);

  const canvas = document.createElement("canvas");
  canvas.width = cw;
  canvas.height = ch;
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    throw new Error("Could not create export canvas");
  }

  const isTransparent =
    !bgColor || bgColor === "transparent" || bgColor === "rgba(0,0,0,0)";

  // Keep bleed pads transparent so post-Apply preview can lock the visible
  // frame to the subject size without a larger white canvas around it.
  ctx.clearRect(0, 0, cw, ch);
  if (!isTransparent) {
    ctx.fillStyle = bgColor;
    ctx.fillRect(pad, pad, iw, ih);
  }

  if (backgroundImageUrl) {
    const bgImg = await loadImage(backgroundImageUrl);
    ctx.drawImage(bgImg, pad, pad, iw, ih);
  }

  ctx.save();
  ctx.translate(pad, pad);
  if (shadowEnabled) {
    ctx.filter = buildDropShadowFilter(shadowState);
  }
  ctx.drawImage(img, 0, 0, iw, ih);
  ctx.restore();

  return encodeCanvasToPngBlob(canvas);
}

export async function exportOutlinePrepImageBlob(imageUrl, options = {}) {
  return buildSubjectEffectBlob(imageUrl, options);
}

export async function exportBackgroundFillPrepImageBlob(imageUrl, options = {}) {
  return buildSubjectEffectBlob(imageUrl, {
    ...options,
    shadowState: { enabled: false },
    outlineState: { enabled: false },
  });
}

export async function exportWatermarkPrepImageBlob(imageUrl, { watermark } = {}) {
  const img = await loadImage(imageUrl);
  const iw = img.naturalWidth || img.width;
  const ih = img.naturalHeight || img.height;

  return compositeEditorToPngBlob({
    imageUrl,
    backgroundColor: "transparent",
    canvasSize: { width: iw, height: ih },
    objectSize: { width: iw, height: ih },
    objectPosition: { x: 0, y: 0 },
    objectRotation: 0,
    objectFlip: { x: 1, y: 1 },
    cropState: { x: 0, y: 0, width: iw, height: ih },
    shadowState: { enabled: false },
    outlineState: { enabled: false },
    watermarkState: {
      ...watermark,
      enabled: true,
      embed: true,
    },
    softOverflow: true,
    exportSize: "standard",
  });
}

/**
 * Bake Editing Tools → Blur (Photoroom-style).
 * Supports full / subject / background targets + Gaussian mix & edge preserve.
 * @param {string} sourceImageUrl - Original uploaded image
 * @param {object} options
 */
export async function exportBlurPrepImageBlob(sourceImageUrl, options = {}) {
  const type = options.type || "gaussian";
  const amount = Math.max(0, Math.round(Number(options.amount) || 0));
  const cutoutUrl = options.cutoutUrl || null;
  const blurTarget =
    options.blurTarget || (cutoutUrl ? "subject" : "full");
  const effectOpts = {
    type,
    amount,
    pixelShape: options.pixelShape,
    pixelGap: options.pixelGap,
    pixelSoftness: options.pixelSoftness,
    pixelMix: options.pixelMix,
    blurTarget,
    gaussianMix: options.gaussianMix,
    edgePreserve: options.edgePreserve,
  };

  const source = await loadImage(sourceImageUrl);
  const iw = source.naturalWidth || source.width;
  const ih = source.naturalHeight || source.height;
  if (!iw || !ih) {
    throw new Error("Invalid image dimensions");
  }

  const sourceCanvas = document.createElement("canvas");
  sourceCanvas.width = iw;
  sourceCanvas.height = ih;
  const sctx = sourceCanvas.getContext("2d");
  if (!sctx) throw new Error("Could not create canvas");
  sctx.drawImage(source, 0, 0, iw, ih);

  let cutoutCanvas = null;
  if (cutoutUrl) {
    const cutout = await loadImage(cutoutUrl);
    cutoutCanvas = document.createElement("canvas");
    cutoutCanvas.width = iw;
    cutoutCanvas.height = ih;
    const cctx = cutoutCanvas.getContext("2d");
    if (!cctx) throw new Error("Could not create canvas");
    cctx.drawImage(cutout, 0, 0, iw, ih);
  }

  const composed = composeBlurPrepCanvas(sourceCanvas, cutoutCanvas, effectOpts);
  return encodeCanvasToPngBlob(composed);
}
